import React, { useState } from 'react'; // Import useState
import FloatingMenu from '../FloatingMenu';
import { Box, Paper, Typography, Grid, ListItemIcon, Container, Modal, IconButton, Fade, Backdrop } from '@mui/material'; // Import Modal, IconButton, Fade, Backdrop
import { motion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search'; // Import Magnify Icon

// --- Import Updated Icons (Ensure these paths/names are correct) ---
import IconDiscovery from '../../assets/images/icon-search.svg'; // Magnifying glass / Recipe book
import IconDashboard from '../../assets/images/icon-dashboard.svg'; // Dashboard / Info panel
import IconReviews from '../../assets/images/icon-reviews.svg'; // Star rating / Speech bubble
import IconNutrition from '../../assets/images/icon-nutrition.svg'; // Apple / Chart
import IconCustomize from '../../assets/images/icon-customize.svg'; // Wrench / Mixer
import IconVoice from '../../assets/images/icon-voice.svg'; // Microphone
import IconContext from '../../assets/images/icon-context.svg'; // Brain / Chat flow
import IconQA from '../../assets/images/icon-qa.svg'; // Question mark / Lightbulb

// --- Import Feature Images ---
import FeatureSemanticSearch from '../../assets/images/features/feature_semantic_search.png';
import FeatureRecipeDashboard from '../../assets/images/features/feature_recipe_dashboard.png';
import FeatureReviewDashboard from '../../assets/images/features/feature_review_dashboard.png';
import FeatureNutritionViz from '../../assets/images/features/feature_nutrition_viz.png';
import FeatureCustomization from '../../assets/images/features/feature_customization.png';
// Using a placeholder image for voice input since the file is missing
import FeatureContextChat from '../../assets/images/features/feature_context_chat.png';
import FeatureGroundedQA from '../../assets/images/features/feature_grounded_qa.png';
// Import placeholder image (using one of the existing numbered images)
import PlaceholderImage from '../../assets/images/features/5.png';

// Define styles for icons
const iconStyle = {
    filter: 'invert(20%) sepia(51%) saturate(5000%) hue-rotate(280deg) brightness(95%) contrast(90%)', // Keep your purple theme
    width: '24px',
    height: '24px',
    marginRight: '16px',
};

// Define styles for the image container
const imageContainerStyle = {
    height: '550px', // Increased image height as requested
    width: '100%',
    backgroundColor: '#e8e8e8', // Lighter placeholder background
    borderRadius: '8px',
    marginBottom: '16px',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative', // For potential overlay text or icons later
    cursor: 'pointer', // Indicate clickable
    '&:hover .magnify-icon': { // Show icon on hover
        opacity: 1,
    },
};

const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'contain', // Use 'contain' to show the whole image/screenshot
    padding: '5px', // Add padding if needed for screenshots
};

const magnifyIconStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: '50%',
    padding: '8px',
    opacity: 0, // Hidden by default
    transition: 'opacity 0.3s ease-in-out',
    pointerEvents: 'none', // Allow clicks to pass through to the container
};

// --- Updated Feature Points based on your LangGraph Implementation ---
const featurePoints = [
  {
    icon: IconDiscovery,
    title: 'Semantic Recipe Discovery',
    description: 'Find recipes by meaning, not just keywords. Ask "Find me vegetarian soup recipes and tag the recipes. 5 recipes" and get relevant ideas using RAG & Vector Search for intuitive exploration.',
    alt: 'Semantic Search icon',
    image: FeatureSemanticSearch
  },
  {
    icon: IconDashboard,
    title: 'Detailed Recipe Dashboard',
    description: 'View comprehensive recipe details: ingredients, steps, time, difficulty, and source. Everything is presented clearly using structured output for easy understanding.',
    alt: 'Recipe Dashboard icon',
    image: FeatureRecipeDashboard
  },
  {
    icon: IconReviews,
    title: 'Review & Rating Insights',
    description: 'Make informed choices with aggregated user ratings, sentiment analysis (positive/negative), and key review snippets fetched via Function Calling, summarizing community feedback.',
    alt: 'Reviews icon',
    image: FeatureReviewDashboard
  },
  {
    icon: IconNutrition,
    title: 'Nutrition Analysis & Viz',
    description: 'Understand ingredient nutrition (per 100g avg). Fetches data via USDA API, aggregates it, and visualizes % Daily Values, helping you track dietary goals.',
    alt: 'Nutrition Analysis icon',
    image: FeatureNutritionViz
  },
  {
    icon: IconCustomize,
    title: 'Recipe Customization',
    description: 'Need it vegan, low-fat, or require a substitute? Ask the assistant for modification suggestions, powered by Function Calling for smart, context-aware changes.',
    alt: 'Customization icon',
    image: FeatureCustomization
  },
  {
    icon: IconVoice,
    title: 'Voice Command Input',
    description: 'Hands full? Interact naturally using voice commands. Your speech is transcribed accurately using state-of-the-art Speech-to-Text for convenient, hands-free operation.',
    alt: 'Voice Input icon',
    image: PlaceholderImage  // Using placeholder image until feature_voice_input.png is available
  },
  {
    icon: IconContext,
    title: 'Context-Aware Conversation',
    description: 'The assistant remembers the recipe you\'re discussing. Ask follow-up questions about details, reviews, or nutrition seamlessly, thanks to LangGraph state management.',
    alt: 'Context icon',
    image: FeatureContextChat
  },
  {
    icon: IconQA,
    title: 'Grounded Cooking Q&A',
    description: 'Ask general cooking questions ("substitute for buttermilk?") and get reliable answers backed by Google Search grounding, ensuring trustworthy culinary advice.',
    alt: 'Q&A icon',
    image: FeatureGroundedQA
  },
];

// Animation variants (keep as is or adjust timing)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1, // Slightly faster stagger for 8 items
    },
  },
};

const itemVariants = {
  hidden: { y: -30, opacity: 0 }, // Slightly less dramatic Y shift
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 100, // Slightly softer spring
    },
  },
};

// Modal style
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '90vw', // Limit width
  maxHeight: '90vh', // Limit height
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 1, // Padding around the image
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const Features = () => {
  // State for Modal
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleOpen = (imageSrc) => {
    setSelectedImage(imageSrc);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  return (
    <Box className="page-section" sx={{ py: { xs: 4, md: 6 }, background: '#f8f8f8' }}> {/* Slightly lighter background */}
      <FloatingMenu />
      <Container maxWidth="lg">
        {/* Title Box */}
        <Paper
          elevation={2}
          sx={{
            padding: { xs: 2, md: 3 },
            borderRadius: '12px',
            background: 'linear-gradient(to right, #d1fecb, #a8e0a0)', // Gradient background
            mb: 4, // Adjusted margin
            textAlign: 'center',
          }}
        >
          <Typography variant="h3" component="h1" sx={{ color: '#135000', fontWeight: 'bold' }}>
            Assistant Capabilities 💡
          </Typography>
        </Paper>

        {/* Introduction Text Box */}
        <Paper
          elevation={3}
          sx={{
            padding: { xs: 3, md: 4 },
            borderRadius: '18px',
            background: 'linear-gradient(120deg, #f8fafc 70%, #e0aaff 100%)',
            mb: 7,
            borderLeft: '7px solid',
            borderColor: '#6459CF', // Deep lavender accent
            boxShadow: '0 6px 32px 0 rgba(100,89,207,0.08)',
            maxWidth: '900px',
            mx: 'auto',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Typography variant="h4" component="h2" sx={{ color: '#27173A', fontWeight: 700, mb: 2, fontFamily: 'Montserrat, Segoe UI, Arial, sans-serif', letterSpacing: '0.5px' }}>
            Introducing Chefbelle: Cooking, Reimagined
          </Typography>
          <Typography variant="body1" sx={{ mb: 2.5, color: '#3a3551', fontSize: '1.18rem', fontWeight: 400, lineHeight: 1.7 }}>
            What if you had a smart kitchen companion who understood your reality? Meet <b>Chefbelle</b>, an AI-powered assistant designed to transform your cooking experience. Chefbelle helps you:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: '#4B0082', fontSize: '1.08rem', fontWeight: 500, lineHeight: 1.7, '& li': { mb: 1.2, fontFamily: 'Georgia, serif', letterSpacing: '0.1px' }, maxWidth: '700px' }}>
            <li>🍳 Discover delicious recipes based on the ingredients you already have.</li>
            <li>🥗 Customize meals to fit your dietary goals (Vegan? Gluten-free? Low-carb? No problem!).</li>
            <li>📝 Receive clear, step-by-step cooking guidance.</li>
            <li>📊 Understand the nutritional impact of your meals.</li>
            <li>🗣️ Interact naturally using voice or text commands.</li>
          </Box>
          <Typography variant="h6" component="h3" sx={{ color: '#6459CF', fontWeight: 700, mb: 1.5, letterSpacing: '0.5px', fontFamily: 'Montserrat, Segoe UI, Arial, sans-serif' }}>
            Powered by Generative AI
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#3a3551', fontSize: '1.08rem', fontWeight: 400 }}>
            Chefbelle leverages a suite of cutting-edge Generative AI capabilities to provide a seamless and intelligent experience:
          </Typography>
          <Box component="ul" sx={{ pl: 3, color: '#555', fontSize: '1.01rem', '& li': { mb: 0.7, fontFamily: 'Georgia, serif' }, columnCount: { xs: 1, sm: 2 }, maxWidth: '800px' }}>
            <li>🧠 LLM Core (Gemini): Powers natural language understanding and response generation.</li>
            <li>🗣️ Audio Understanding: Processes voice commands via Speech-to-Text (Whisper/Google Speech).</li>
            <li>🔧 Function Calling: Enables Chefbelle to interact with databases and APIs (like USDA Nutrition).</li>
            <li>🤖 Agents (LangGraph): Orchestrates complex conversational flows and tool usage.</li>
            <li>🌐 Grounding: Uses Google Search for real-time answers to general cooking questions.</li>
            <li>🔍 Embeddings & Vector DB (ChromaDB): Enables semantic search for finding relevant recipes.</li>
            <li>📚 Retrieval Augmented Generation (RAG): Fetches recipe and nutrition data to ground responses in facts, reducing hallucination.</li>
            <li>⚙️ Structured Output (JSON): Facilitates reliable data exchange between the agent and its tools.</li>
            <li>💾 Stateful Conversation: Remembers context across turns for a natural dialogue.</li>
            <li>📈 GenAI Evaluation: Focuses on reasoned, data-driven responses over simple generation.</li>
          </Box>
        </Paper>


        {/* Grid container wrapped with motion.div */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={3} justifyContent="center" alignItems="stretch"> {/* Added alignItems="stretch" */}
            {featurePoints.map((point, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index} component={motion.div} variants={itemVariants}>
                <Paper
                  elevation={3}
                  sx={{
                    padding: 2, // Adjusted padding
                    borderRadius: '12px',
                    background: '#ffffff', // White card background
                    height: '100%', // Make paper fill grid item height
                    display: 'flex', // Use flexbox
                    flexDirection: 'column', // Stack items vertically
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out', // Hover effect
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  {/* Title and Icon Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                      <img src={point.icon} alt={point.alt} style={iconStyle} />
                    </ListItemIcon>
                    <Typography variant="h6" component="h3" sx={{ color: '#135000', fontWeight: 600, fontSize: '1.05rem' }}> {/* Adjusted font */}
                      {point.title}
                    </Typography>
                  </Box>

                  {/* Image Container */}
                  <Box sx={imageContainerStyle} onClick={() => handleOpen(point.image)}>
                    <img
                      src={point.image}
                      alt={`${point.title} illustration`}
                      style={imageStyle}
                      // Basic error handling for images
                      onError={(e) => {
                        e.target.style.display = 'none'; // Hide broken image icon
                        const parent = e.target.parentElement;
                        if (parent && !parent.querySelector('.placeholder-text')) { // Prevent adding multiple texts
                          const placeholder = document.createElement('span');
                          placeholder.textContent = 'Visual coming soon!';
                          placeholder.className = 'placeholder-text';
                          placeholder.style.color = '#888';
                          placeholder.style.fontSize = '0.9rem';
                          parent.appendChild(placeholder);
                          parent.style.cursor = 'default'; // Remove pointer cursor if image fails
                          parent.onclick = null; // Disable click if image fails
                        }
                      }}
                    />
                     <IconButton className="magnify-icon" sx={magnifyIconStyle}>
                        <SearchIcon fontSize="large" />
                    </IconButton>
                  </Box>

                  {/* Description Text */}
                  <Typography variant="body2" sx={{ color: '#555', mt: 'auto', fontSize: '0.85rem', lineHeight: 1.5, flexGrow: 1, pt: 1 }}> {/* Added flexGrow and padding top */}
                    {point.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Updated GenAI powered text */}
        <Paper elevation={1} sx={{ mt: 6, p: 2, background: '#e8e8e8', borderRadius: '8px' }}>
          <Typography variant="body1" align="center" sx={{ color: '#444', fontStyle: 'italic' }}>
            Powered by: LangGraph Agents, Function Calling, RAG (ChromaDB), Audio Understanding, Grounding, Structured Output & more.
          </Typography>
        </Paper>

      </Container>

       {/* Image Modal */}
       <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={open}
            onClose={handleClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }} // Updated syntax for MUI v5+
            slotProps={{
                backdrop: {
                    timeout: 500,
                },
            }}
        >
            <Fade in={open}>
                <Box sx={modalStyle}>
                    <img
                        src={selectedImage}
                        alt="Enlarged feature"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                </Box>
            </Fade>
        </Modal>
    </Box>
  );
}

export default Features;